// apps/mcv-client/src/components/Layout/MobileMenu.tsx
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';
import { HeaderBrandMenu } from '~/components/BrandSwitcher';
import { HlxLogo, McvLogo } from '~/components/logos';
import { BRAND_CONFIGS } from '~/config/branding';
import { paths } from '~/routing/routes';
import { VaultSideMenu } from './SideNav/VaultSideMenu';
import type { RenderSideMenu } from './SideNav/types';

interface Props {
  logo?: 'mcv' | 'hlx';
  renderSideMenu?: RenderSideMenu;
  /** Optional role-aware destination for the other brand. */
  switchTargetPath?: string;
  allowBrandSwitch?: boolean;
}

export const MobileMenu = ({
  logo = 'mcv',
  renderSideMenu,
  switchTargetPath,
  allowBrandSwitch = true,
}: Props) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const closeMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  // Lock scroll, handle Escape, trap focus inside the drawer while open
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';

    const drawer = menuRef.current;
    const getFocusable = (): HTMLElement[] => {
      if (!drawer) return [];
      return Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
        )
      ).filter((el) => !el.hasAttribute('inert'));
    };

    closeButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenu();
        return;
      }
      if (e.key !== 'Tab' || !drawer) return;
      const focusables = getFocusable();
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedRef.current?.focus();
    };
  }, [isMobileMenuOpen, closeMenu]);

  const sideMenu = renderSideMenu ? (
    renderSideMenu({ keyPrefix: 'mobile', onCollapse: closeMenu })
  ) : (
    <VaultSideMenu keyPrefix="mobile" switchTargetPath={switchTargetPath} />
  );

  return (
    <>
      {/* Hamburger button. [MCV-1752] 1rem below the top bar, which starts under the
          impersonation banner when one is shown. */}
      <button
        type="button"
        aria-label="Open navigation menu"
        aria-expanded={isMobileMenuOpen}
        className="max-[1360px]:block min-[1360px]:hidden fixed top-[calc(var(--impersonation-banner-height,0px)+1rem)] left-4 z-30 bg-primary-t-900 border border-primary-t-700 rounded-lg p-2 transition-colors hover:bg-primary-t-800"
        onClick={() => setIsMobileMenuOpen(true)}
      >
        <Bars3Icon className="size-5 text-grey-200" aria-hidden="true" />
      </button>

      {/* Overlay + Drawer. [MCV-1752] Starts under the impersonation banner, so the banner and its
          Exit Impersonation button stay visible and clickable while the drawer is open. The
          backdrop and the drawer fill this box, so they follow it. With no banner the offset is 0. */}
      {isMobileMenuOpen && (
        <div
          className="max-[1360px]:block min-[1360px]:hidden fixed inset-x-0 bottom-0 top-[var(--impersonation-banner-height,0px)] z-50"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-200"
            onClick={closeMenu}
          />
          {/* Drawer */}
          <div
            ref={menuRef}
            className="absolute left-0 top-0 h-full w-80 bg-primary-t-900 shadow-lg overflow-hidden flex flex-col transition-transform duration-300 ease-out"
          >
            <div className="flex items-center justify-between p-3 border-b border-primary-t-700 shrink-0">
              {/* MCV-1513 brand switcher trigger, wrapping the existing logo */}
              <HeaderBrandMenu
                brand={logo}
                allowBrandSwitch={allowBrandSwitch}
                surface="mobile-menu"
                // This drawer runs its own focus trap scoped to `menuRef`, so
                // the panel must stay inside it rather than being portalled to
                // document.body — otherwise Tab walks straight out of the modal.
                inline
                primaryPath={logo === 'hlx' ? paths.hlx.dashboard : paths.vault.dashboard}
                primaryLabel="Go to dashboard"
                triggerAriaLabel={`${BRAND_CONFIGS[logo].siteName} menu`}
                switchTargetPath={switchTargetPath}
              >
                {logo === 'hlx' ? (
                  <HlxLogo label="icon" height={26} />
                ) : (
                  <McvLogo label="side" height={26} />
                )}
              </HeaderBrandMenu>
              <button
                ref={closeButtonRef}
                type="button"
                aria-label="Close navigation menu"
                onClick={closeMenu}
                className="p-1.5 rounded-md hover:bg-white/30 transition-colors"
              >
                <XMarkIcon
                  className="size-5 text-grey-400 hover:text-grey-100 transition-colors duration-150"
                  aria-hidden="true"
                />
              </button>
            </div>
            <div className="flex-1 min-h-0">{sideMenu}</div>
          </div>
        </div>
      )}
    </>
  );
};
